/* eslint-disable react-hooks/exhaustive-deps */
import * as THREE from "three";
import React, { useMemo, useState } from 'react';
import { useThree } from '@react-three/fiber';

import { const_var, params } from '../../../../../../../redux/reducers/reducer';
import { controls } from '../../../../../../../redux/reducers/common/eventReducer';

import { CSG } from "../../../../../../../assets/libs/csg-lib";
import { extrudeSettings, roofPattern, roofPatternCurve, textureAnisotropy } from '../../../../../../../utils/Functions';
import useMouseEvents from '../../../../../../../hooks/useMouseEvents';
import { MaterialInnerRoof, MaterialOuterRoof, MaterialRoofRidge } from '../../../../../CustomMaterial';

const RoofModel = () => {
    const { gl } = useThree();

    const [overHangWidth, setOverHangWidth] = useState(0);
    const [overHangLength, setOverHangLength] = useState(0);
    const [patternLength, setPatternLength] = useState(0);
    const [patternDepth, setPatternDepth] = useState(params.leanR_p_d);

    const canvasElement = document.getElementsByClassName("buildingcanvas")
    const isPattern = (canvasElement != undefined && canvasElement[0]?.clientWidth > 1050) ? true : false
    
    const roofThickness = 0.005;
    const wallThickness = 0.005
    const ridgeThickness = 0.022;

    const sectionW = params.leanR_p_w + 0.35;
    const roofPitchHeight = (params.b_l_t_r_pR / 12) * sectionW / 2;
    const roofAngle = Math.atan((roofPitchHeight) / (sectionW / 2));
    const roofCutShapeWidth = Math.sqrt(Math.pow(params.leanR_p_w, 2) * 2)
    const leanRoofWidth = Math.sqrt(Math.pow((sectionW / 2), 2) + Math.pow(roofPitchHeight, 2));
    const diagonalLength = overHangWidth + leanRoofWidth;
    
    let regularHeight;
    if(params.p_r_s === 1) regularHeight = const_var.roofRegularDst * 2;
    else regularHeight = 0;
    
    const texture_metalSprite = const_var.metalTexture;

    const verticalMap = texture_metalSprite.clone();
    textureAnisotropy(gl, verticalMap, 1.3, 1.3, Math.PI);
    verticalMap.needsUpdate = true;
    const horizontalMap = texture_metalSprite.clone();
    textureAnisotropy(gl, horizontalMap, 1.3, 1.3, Math.PI / 2);
    horizontalMap.offset.setY(params.o_v_h_n_g_e ? 0.5 : 0.15)
    horizontalMap.needsUpdate = true;

    const roofTexture = useMemo(() => {
        if (params.p_r_s === 1 || params.p_r_s === 2) {
            return verticalMap;
        } else if (params.p_r_s === 3) {
            return horizontalMap;
        }
    }, [horizontalMap, params.p_r_s, verticalMap]);

    let sideOverHangSize = 0 
    let endOverHangSize = 0 
    if(const_var.isOverhang['side'] !== undefined && const_var.isOverhang['side'].size){
        sideOverHangSize = Number(const_var.isOverhang['side'].size)
        if(sideOverHangSize<=1){
            sideOverHangSize = (0.25 + sideOverHangSize)
        }
    }
    if(const_var.isOverhang['end'] !== undefined && const_var.isOverhang['end'].size){
        endOverHangSize = Number(const_var.isOverhang['end'].size)
    }
  
    const roofPanModel = useMemo(() => {
        // This model is used only when the roofStyle is not "Regular"
        const model = new THREE.Shape();
        model.moveTo(-(params.leanR_p_d / 2 + ((params.p_r_s === 1) ? overHangLength / 2 : overHangLength)), overHangWidth);
        model.lineTo((params.leanR_p_d / 2 + params.p_w / 2), 0 + overHangWidth);
        model.lineTo((params.leanR_p_d / 2 + params.p_w / 2), -leanRoofWidth);
        model.lineTo(-(params.leanR_p_d / 2 + ((params.p_r_s === 1) ? overHangLength / 2 : overHangLength)), -leanRoofWidth);
        model.closePath();

        return model;
    }, [params.p_r_s, params.leanR_p_d, overHangLength, params.p_w, overHangWidth, leanRoofWidth, params.leanR_p_w, params.b_l_t_r_pR]);

    const roofRegularRoofInnerModel = useMemo(() => {
        const pitchHeightOffset = const_var.roofRegularDst * (params.b_l_t_r_pR / 12);
        const roofSpace = 0.025;

        const model = new THREE.Shape();
        model.moveTo(-sectionW / 2 + roofSpace, -0.01);
        model.lineTo(-sectionW / 2 + roofSpace, const_var.roofRegularDst);
        model.quadraticCurveTo( -sectionW / 2 + roofSpace, const_var.roofRegularDst * 1.8 + pitchHeightOffset / 10 - roofSpace,
                                -sectionW / 2 + roofSpace + const_var.roofRegularDst, const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.b_l_t_r_pR / 12 - roofSpace);
        model.lineTo(0, (params.b_l_t_r_pR / 12) * sectionW / 2 + const_var.roofRegularDst * 2 - roofSpace);
        model.lineTo(sectionW / 2 - roofSpace - const_var.roofRegularDst, const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.b_l_t_r_pR / 12 - roofSpace);
        model.quadraticCurveTo( sectionW / 2 - roofSpace, const_var.roofRegularDst * 1.8 + pitchHeightOffset / 10 - roofSpace,
                                sectionW / 2 - roofSpace, const_var.roofRegularDst);
        model.lineTo(sectionW / 2 - roofSpace, -0.01);

        model.lineTo(sectionW / 2 - roofSpace - roofThickness, -0.01);

        model.lineTo(sectionW / 2 - roofSpace - roofThickness, const_var.roofRegularDst - roofSpace);
        model.quadraticCurveTo( sectionW / 2 - roofSpace - roofThickness, const_var.roofRegularDst * 1.8 + pitchHeightOffset / 10 - roofSpace - roofThickness,
                                sectionW / 2 - roofSpace - const_var.roofRegularDst - roofThickness, const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.b_l_t_r_pR / 12 - roofSpace - roofThickness);
        model.lineTo(0, (params.b_l_t_r_pR / 12) * sectionW / 2 + const_var.roofRegularDst * 2 - roofSpace - roofThickness);
        model.lineTo(-(sectionW / 2 - roofSpace - const_var.roofRegularDst - roofThickness), const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.b_l_t_r_pR / 12 - roofSpace - roofThickness)
        model.quadraticCurveTo( -(sectionW / 2 - roofSpace - roofThickness), const_var.roofRegularDst * 1.8 + pitchHeightOffset / 10 - roofSpace - roofThickness,
                                -(sectionW / 2 - roofSpace - roofThickness), const_var.roofRegularDst - roofSpace)
        model.lineTo(-(sectionW / 2 - roofSpace - roofThickness), -0.01);
        model.closePath();

        return model;
    }, [params.leanR_p_w, params.b_l_t_r_pR])
    
    const topRidgeModel = useMemo(() => {
        const ridgeWidth = 0.25;
        
        const model = new THREE.Shape();
        model.moveTo(-ridgeWidth, (params.b_l_t_r_pR / 12) * (params.leanR_p_w / 2 - ridgeWidth));
        model.lineTo(0, (params.b_l_t_r_pR / 12) * params.leanR_p_w / 2 + regularHeight);
        model.lineTo(ridgeWidth, (params.b_l_t_r_pR / 12) * (params.leanR_p_w / 2 - ridgeWidth));
        model.lineTo(ridgeWidth, (params.b_l_t_r_pR / 12) * (params.leanR_p_w / 2 - ridgeWidth) + (isPattern ? ridgeThickness * 3 : ridgeThickness));
        model.lineTo(0, (params.b_l_t_r_pR / 12) * params.leanR_p_w / 2 + regularHeight + (isPattern ? ridgeThickness * 3 : ridgeThickness));
        model.lineTo(-ridgeWidth, (params.b_l_t_r_pR / 12) * (params.leanR_p_w / 2 - ridgeWidth) + (isPattern ? ridgeThickness * 3 : ridgeThickness));
        model.closePath();

        return model;
    }, [params.leanR_p_w, params.b_l_t_r_pR, regularHeight, isPattern]);

    const roofModelObj = useMemo(() => {
        let mainLeftOuter, mainRightOuter, mainLeftInner, mainRightInner, curveLeftOuter, curveRightOuter, innerRegularRoof, topRidge;

        const pitchHeightOffset = const_var.roofRegularDst * (params.p_r_p / 12);

        const centerRoofDelModel = new THREE.Shape();
        centerRoofDelModel.moveTo(-params.p_w / 2, 0);
        if (params.p_r_s === 1) {
            centerRoofDelModel.lineTo(-params.p_w / 2, params.p_h + const_var.roofRegularDst);
            centerRoofDelModel.quadraticCurveTo(
                -params.p_w / 2, params.p_h + const_var.roofRegularDst * 1.8 + pitchHeightOffset / 10,
                -params.p_w / 2 + const_var.roofRegularDst, params.p_h + const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.p_r_p / 12
            );
            centerRoofDelModel.lineTo(0, params.p_h + (params.p_r_p / 12) * params.p_w / 2 + const_var.roofRegularDst * 2);
            centerRoofDelModel.lineTo(params.p_w / 2 - const_var.roofRegularDst, params.p_h + const_var.roofRegularDst * 2 + const_var.roofRegularDst * params.p_r_p / 12);
            centerRoofDelModel.quadraticCurveTo(
                params.p_w / 2, params.p_h + const_var.roofRegularDst * 1.8 + pitchHeightOffset / 10,
                params.p_w / 2, params.p_h + const_var.roofRegularDst
            );
            centerRoofDelModel.lineTo(params.p_w / 2, 0);
        } else {
            centerRoofDelModel.lineTo(-params.p_w / 2, params.p_h);
            centerRoofDelModel.lineTo(0, params.p_h + params.p_w / 2 * params.p_r_p / 12);
            centerRoofDelModel.lineTo(params.p_w / 2, params.p_h);
            centerRoofDelModel.lineTo(params.p_w / 2, 0);
        }
        centerRoofDelModel.closePath();
        
        const outerRoofMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPattern(patternLength, isPattern, "roof"), extrudeSettings(patternDepth)));
        const innerRoofMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPanModel, extrudeSettings(roofThickness)));
        const innerRegularRoofMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofRegularRoofInnerModel, extrudeSettings(patternDepth)));
        const curveOuterMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(roofPatternCurve(1, params.b_l_t_r_pR, isPattern), extrudeSettings(patternDepth)));
        const centerRoofDelMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(centerRoofDelModel, extrudeSettings(params.p_d * 2)));
        const topRidgeModelMesh = new THREE.Mesh(new THREE.ExtrudeGeometry(topRidgeModel, extrudeSettings(patternLength)));

        let leftOuterRoofMeshPosUpdate = outerRoofMesh.clone();
        leftOuterRoofMeshPosUpdate.position.set((params.p_r_s === 3 ? 0 : patternDepth), params.leanR_p_h + roofPitchHeight + regularHeight + (params.p_r_s === 1 ? 0 : 0.07), (params.p_r_s === 1) ? 0 : wallThickness * (params.b_l_t_r_pR / 12));
        leftOuterRoofMeshPosUpdate.rotation.copy(new THREE.Euler(roofAngle , (params.p_r_s === 3 ? 0 : -Math.PI / 2), 0, 'XYZ'))
        leftOuterRoofMeshPosUpdate.updateMatrixWorld();
        
        let rightOuterRoofMeshPosUpdate = outerRoofMesh.clone();
        rightOuterRoofMeshPosUpdate.position.set( (params.p_r_s === 3 ? patternLength : 0), params.leanR_p_h + roofPitchHeight + regularHeight + (params.p_r_s === 1 ? 0 : 0.07), (params.p_r_s === 1) ?  0 : -wallThickness * (params.b_l_t_r_pR / 12));
        rightOuterRoofMeshPosUpdate.rotation.copy(new THREE.Euler(-roofAngle , (params.p_r_s === 3 ? Math.PI : Math.PI / 2), 0, 'XYZ'))
        rightOuterRoofMeshPosUpdate.updateMatrixWorld();

        let leftInnerRoofMeshPosUpdate = innerRoofMesh.clone();
        leftInnerRoofMeshPosUpdate.position.set((params.p_w / 2 + params.leanR_p_d / 2 + wallThickness * 2), params.leanR_p_h + 0.05 - roofThickness, params.leanR_p_w / 2 + 0.175);
        leftInnerRoofMeshPosUpdate.rotation.set(Math.PI / 2 + roofAngle, Math.PI, 0);
        leftInnerRoofMeshPosUpdate.updateMatrixWorld();

        let rightInnerRoofMeshPosUpdate = innerRoofMesh.clone();
        rightInnerRoofMeshPosUpdate.position.set((params.p_w / 2 + params.leanR_p_d / 2 + wallThickness * 2), params.leanR_p_h + 0.05, -params.leanR_p_w / 2 - 0.175);
        rightInnerRoofMeshPosUpdate.rotation.set(-Math.PI / 2 - roofAngle, Math.PI, 0);
        rightInnerRoofMeshPosUpdate.updateMatrixWorld();

        let innerRegularRoofMeshPosUpdate = innerRegularRoofMesh.clone();
        innerRegularRoofMeshPosUpdate.position.set(0, params.leanR_p_h, 0);
        innerRegularRoofMeshPosUpdate.rotation.set(0, Math.PI / 2, 0);
        innerRegularRoofMeshPosUpdate.updateMatrixWorld();

        let curveLeftOuterMeshPosUpdate = curveOuterMesh.clone();
        curveLeftOuterMeshPosUpdate.position.set(0, params.leanR_p_h, params.leanR_p_w / 2 + 0.165);
        curveLeftOuterMeshPosUpdate.rotation.set(0, Math.PI / 2, 0);
        curveLeftOuterMeshPosUpdate.updateMatrixWorld();

        let curveRightOuterMeshPosUpdate = curveOuterMesh.clone();
        curveRightOuterMeshPosUpdate.position.set(patternDepth, params.leanR_p_h, -params.leanR_p_w / 2 - 0.165);
        curveRightOuterMeshPosUpdate.rotation.set(0, -Math.PI / 2, 0);
        curveRightOuterMeshPosUpdate.updateMatrixWorld();

        let centerRoofDelMesh_Temp = centerRoofDelMesh.clone();
        centerRoofDelMesh_Temp.position.set(0, 0.012, -params.p_d);
        centerRoofDelMesh_Temp.updateMatrixWorld();

        let topRidgeModelMesh_Temp = topRidgeModelMesh.clone();
        topRidgeModelMesh_Temp.position.set((params.p_w / 2 + params.leanR_p_d + overHangLength), params.leanR_p_h + 0.1 * (params.b_l_t_r_pR / 12) + 0.073 + ridgeThickness, 0);
        topRidgeModelMesh_Temp.rotation.set(0, -Math.PI / 2, 0)
        topRidgeModelMesh_Temp.updateMatrixWorld();

        const leftOuterRoofMesh_CSG = CSG.fromMesh(leftOuterRoofMeshPosUpdate);
        const rightOuterRoofMesh_CSG = CSG.fromMesh(rightOuterRoofMeshPosUpdate);
        const leftInnerRoofMesh_CSG = CSG.fromMesh(rightInnerRoofMeshPosUpdate);
        const rightInnerRoofMesh_CSG = CSG.fromMesh(leftInnerRoofMeshPosUpdate);
        const innerRegularRoofMesh_CSG = CSG.fromMesh(innerRegularRoofMeshPosUpdate);
        const curveLeftOuterMesh_CSG = CSG.fromMesh(curveLeftOuterMeshPosUpdate);
        const curveRightOuterMesh_CSG = CSG.fromMesh(curveRightOuterMeshPosUpdate);
        const centerRoofDelMesh_CSG = CSG.fromMesh(centerRoofDelMesh_Temp);
        const topRidgeModelMesh_CSG = CSG.fromMesh(topRidgeModelMesh_Temp);

        const roofLeftOuterMeshCSG = leftOuterRoofMesh_CSG.subtract(centerRoofDelMesh_CSG);
        const roofRightOuterMeshCSG = rightOuterRoofMesh_CSG.subtract(centerRoofDelMesh_CSG);
        const roofLeftInnerMeshCSG = leftInnerRoofMesh_CSG.subtract(centerRoofDelMesh_CSG);
        const roofRightInnerMeshCSG = rightInnerRoofMesh_CSG.subtract(centerRoofDelMesh_CSG);
        const innerRegularRoofMeshCSG = innerRegularRoofMesh_CSG.subtract(centerRoofDelMesh_CSG);
        const topRidgeModelMeshCSG = topRidgeModelMesh_CSG.subtract(centerRoofDelMesh_CSG);
        const curveLeftOuterMeshCSG = curveLeftOuterMesh_CSG.subtract(centerRoofDelMesh_CSG);
        const curveRightOuterMeshCSG = curveRightOuterMesh_CSG.subtract(centerRoofDelMesh_CSG);

        const roofLeftOuterMeshFinal = CSG.toMesh(roofLeftOuterMeshCSG,new THREE.Matrix4());
        const roofRightOuterMeshFinal = CSG.toMesh(roofRightOuterMeshCSG,new THREE.Matrix4());
        const roofLeftInnerMeshFinal = CSG.toMesh(roofLeftInnerMeshCSG, new THREE.Matrix4());
        const roofRightInnerMeshFinal = CSG.toMesh(roofRightInnerMeshCSG, new THREE.Matrix4());
        const curveLeftOuterMeshFinal = CSG.toMesh(curveLeftOuterMeshCSG, new THREE.Matrix4());
        const curveRightOuterMeshFinal = CSG.toMesh(curveRightOuterMeshCSG, new THREE.Matrix4());
        const innerRegularRoofMeshFinal = CSG.toMesh(innerRegularRoofMeshCSG, new THREE.Matrix4());
        const CSGTopRidgeModel = CSG.toMesh(topRidgeModelMeshCSG, new THREE.Matrix4());
            
        mainLeftOuter = roofLeftOuterMeshFinal.geometry;
        mainRightOuter = roofRightOuterMeshFinal.geometry;
        mainLeftInner = roofLeftInnerMeshFinal.geometry;
        mainRightInner = roofRightInnerMeshFinal.geometry;
        curveLeftOuter = curveLeftOuterMeshFinal.geometry;
        curveRightOuter = curveRightOuterMeshFinal.geometry;
        innerRegularRoof = innerRegularRoofMeshFinal.geometry;
        topRidge = CSGTopRidgeModel.geometry;

        return {mainLeftOuter, mainRightOuter, mainLeftInner, mainRightInner, curveLeftOuter, curveRightOuter, innerRegularRoof, topRidge};
    }, [params.leanR_p_h, params.leanR_p_w, params.leanR_p_d, roofPitchHeight, params.b_l_t_r_pR, roofCutShapeWidth, patternLength, patternDepth, params.p_w, params.p_h, params.p_d, params.leanR_p_pos, overHangWidth, params.p_r_s, roofAngle, params.o_v_h_n_g, params.o_v_h_n_g_e, params.p_r_p, sideOverHangSize, endOverHangSize, isPattern])

    useMemo(() => {
        if (params.p_r_s === 1 || params.p_r_s === 2) {
            if (params.p_r_s === 1) {
                setPatternLength(Math.sqrt(Math.pow((sectionW / 2 - const_var.roofRegularDst), 2) + Math.pow(((params.b_l_t_r_pR / 12) * (sectionW / 2 - const_var.roofRegularDst)), 2)));
                setPatternDepth(params.leanR_p_d + params.p_w / 2 + 0.45)
            } else {
                if (params.o_v_h_n_g === true){
                    setOverHangWidth(sideOverHangSize)
                } else {
                    setOverHangWidth(0.75);
                }
                if (params.o_v_h_n_g_e === true) {
                    setOverHangLength(endOverHangSize)
                } else {
                    setOverHangLength(0.75)
                }
                setPatternLength(diagonalLength);
                setPatternDepth(params.leanR_p_d + params.p_w / 2 + overHangLength)
            }
        } else if (params.p_r_s === 3) {
            if (params.o_v_h_n_g === true){
                setOverHangWidth(sideOverHangSize)
            } else {
                setOverHangWidth(0.75);
            }
            if (params.o_v_h_n_g_e === true) {
                setOverHangLength(endOverHangSize)
            } else {
                setOverHangLength(0.75)
            }
            setPatternLength(params.leanR_p_d + overHangLength + params.p_w / 2)
            
            setPatternDepth(diagonalLength)
          }
    }, [params.p_r_s, params.leanR_p_d, params.leanR_p_h, params.leanR_p_w, params.p_w, params.b_l_t_r_pR, params.o_v_h_n_g_e, params.o_v_h_n_g, patternDepth, overHangLength, overHangWidth, diagonalLength, sideOverHangSize, endOverHangSize])

    const handleMouseEvent = (e) => {
        if (controls.isCheckActiveTab === 'doorwindow' && (const_var.focusedSection === "reftlean" && params.activeWall === "R_L_F_S_W")) {
            switch (e.type) {
                case 'pointerdown':
                case 'click':
                case 'pointermove':
                    e.stopPropagation();
                    break;
                default :
                    break
            }
        }
    };
    const eventHandlers = useMouseEvents(handleMouseEvent);
        
    const texture = useMemo(() => {
        let texture = (const_var.wallTextureObj["vertical"] != undefined) ? const_var.wallTextureObj["vertical"].clone() : const_var.wallTextureObj["vertical"];
        textureAnisotropy(gl, texture, 1.3, 1.3, Math.PI/-2);    
        return texture
    }, [params.p_v_w,params.wallTexture])
    
    return (
        <group {...eventHandlers}>
            <>
                <mesh name="roof-out-left" position={[0, 0.02, 0]} geometry={roofModelObj?.mainLeftOuter} renderOrder={1}>
                    <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"gableRightLean"} />
                </mesh>
                <mesh name="roof-out-right" position={[0, 0.02, 0]} geometry={roofModelObj?.mainRightOuter} renderOrder={1}>
                    <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"gableRightLean"} />
                </mesh>
                {params.p_r_s === 1 ?
                    <mesh name="roof-in" geometry={roofModelObj?.innerRegularRoof}>
                        <MaterialInnerRoof texture={roofTexture} visibleSection={'gableRightLean'} />
                    </mesh>
                    :
                    <>
                        <mesh name="roof-in-left" geometry={roofModelObj?.mainLeftInner}>
                            <MaterialInnerRoof texture={roofTexture} visibleSection={'gableRightLean'} />
                        </mesh>
                        <mesh name="roof-in-right" geometry={roofModelObj?.mainRightInner}>
                            <MaterialInnerRoof texture={roofTexture} visibleSection={'gableRightLean'} />
                        </mesh>
                    </>
                }
            </>

            {params.p_r_s === 1 &&
                <>
                    <mesh name="roof-left-out-curve" position={[0, 0.02, 0]} geometry={roofModelObj?.curveLeftOuter} renderOrder={1}>
                        <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"gableRightLean"} />
                    </mesh>
                    <mesh name="roof-right-out-curve" position={[0, 0.02, 0]} geometry={roofModelObj?.curveRightOuter} renderOrder={1}>
                        <MaterialOuterRoof isPattern={isPattern} texture={texture} visibleSection={"gableRightLean"} />
                    </mesh>
                </>
            }
            {params.p_r_s === 3 &&
                <mesh name="top-ridge-model" position={[0, 0.02, 0]} geometry={roofModelObj?.topRidge}>
                    <MaterialRoofRidge isPattern={isPattern} visibleSection={'gableRightLean'} />
                </mesh>
            }
        </group>
    )
}

export default RoofModel